import { useEffect, useState } from "react";
import { apiJson } from "../../utils/api";
import useSeo from "../../hooks/useSeo";

const HOT_CITIES = ["杭州", "九江", "北京", "上海", "南昌", "广州", "深圳", "成都", "武汉", "西安"];

export default function MarriagePlaces() {
  const [city, setCity] = useState("杭州");
  const [district, setDistrict] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [geoHint, setGeoHint] = useState("");

  useSeo({
    title: "附近领证地点 | 婚姻登记处查询",
    metaDescription: "按城市查询婚姻登记机关，并区分正式登记点与仪式场地。",
    path: "/marriage/places",
  });

  const load = async (params = {}) => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    const c = params.city ?? city;
    const d = params.district ?? district;
    if (c) q.set("city", c);
    if (d) q.set("district", d);
    if (params.lat) q.set("lat", params.lat);
    if (params.lng) q.set("lng", params.lng);
    try {
      const { res, data } = await apiJson(`/api/marriage/places?${q.toString()}`);
      if (!res.ok) throw new Error(data?.detail || "查询失败");
      setItems(data.items || []);
      if (!(data.items || []).length) {
        setError("这个城市暂未收录登记点。可点下方热门城市，或换「杭州市」「北京市」再查。");
      }
    } catch (err) {
      setItems([]);
      setError(err.message || "查询失败，请确认领证中心接口已启动");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const useLocation = () => {
    if (!navigator.geolocation) {
      setGeoHint("当前浏览器不支持定位");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoHint("已按定位距离排序（含全国已收录登记点）");
        setCity("");
        load({ lat: pos.coords.latitude, lng: pos.coords.longitude, city: "" });
      },
      () => setGeoHint("定位未授权，可手动输入城市或点热门城市")
    );
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-extrabold">📍 附近领证</h1>
      <p className="text-sm text-gray-500">
        只有标记为「可办理正式婚姻登记」的地点才具有法律登记权限。景区、酒店等仅为仪式场地。
        输入「杭州」或「杭州市」都可以。
      </p>
      <div className="bg-white rounded-2xl p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="城市，如 杭州 / 杭州市 / 九江"
            className="border rounded-xl px-3 py-2"
          />
          <input
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
            placeholder="区县（可选）"
            className="border rounded-xl px-3 py-2"
          />
          <button
            type="button"
            onClick={() => load()}
            className="px-4 py-2 rounded-xl bg-rose-500 text-white"
          >
            查询
          </button>
          <button type="button" onClick={useLocation} className="px-4 py-2 rounded-xl border">
            使用我的位置
          </button>
          {geoHint ? <span className="text-xs text-gray-500">{geoHint}</span> : null}
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {HOT_CITIES.map((name) => (
            <button
              key={name}
              type="button"
              className="px-3 py-1 rounded-full border bg-rose-50"
              onClick={() => {
                setCity(name);
                load({ city: name });
              }}
            >
              {name}
            </button>
          ))}
        </div>
      </div>
      {loading ? <p className="text-gray-400">加载中...</p> : null}
      {error ? <p className="text-sm text-amber-800 bg-amber-50 rounded-xl px-3 py-2">{error}</p> : null}
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((item) => (
          <article key={item.id} className="bg-white rounded-2xl p-5 border shadow-sm space-y-2">
            <div className="flex justify-between gap-2">
              <h2 className="font-bold">{item.name}</h2>
              <span
                className={`text-xs px-2 py-1 rounded-full h-fit ${
                  item.can_legal_register ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"
                }`}
              >
                {item.can_legal_register ? "可办理正式登记" : "仪式/纪念"}
              </span>
            </div>
            <p className="text-sm text-rose-700">{item.legal_label}</p>
            <p className="text-sm text-gray-600">地址：{item.address}</p>
            <p className="text-sm text-gray-600">电话：{item.phone || "请以官网/12345为准"}</p>
            <p className="text-sm text-gray-600">办理业务：{item.business_scope}</p>
            <p className="text-sm text-gray-600">办公时间：{item.opening_hours}</p>
            {item.distance_km != null ? <p className="text-sm">距离约 {item.distance_km} 公里</p> : null}
            <p className="text-xs text-gray-400">
              来源：{item.source_name} · 核验：{item.last_verified_at || "—"}
            </p>
            <div className="flex gap-3 text-sm pt-2">
              {item.official_url ? (
                <a className="text-rose-600" href={item.official_url} target="_blank" rel="noreferrer">
                  查看详情
                </a>
              ) : null}
              {item.nav_url ? (
                <a className="text-rose-600" href={item.nav_url} target="_blank" rel="noreferrer">
                  地图导航
                </a>
              ) : null}
              {item.appointment_url ? (
                <a className="text-rose-600" href={item.appointment_url} target="_blank" rel="noreferrer">
                  预约指引
                </a>
              ) : (
                <span className="text-gray-400">预约请走当地政务网或电话</span>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
